import { useContext, useEffect, useRef, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { Table, Spin } from "antd";
import { DateRangePicker } from "rsuite";
import moment from "moment";
import { useMutation } from "react-query";

import client from "../../api";
import GlobalContext from "src/context/global-context";
import SearchFilter from "src/components/search-filters/reset2fa-filter";
import {
  adminLogsFilterOptions,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { useDateTimePicker } from "src/lib/hooks/useDateTimePicker";
import { currentTimeForDataPicker, getErrorMessage } from "src/lib/utils";
import { PaginationInput } from "src/api/types";

const ActivityLogs = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  const { canView, canList } = usePermissions(
    permissionConstants.ACTIVITY_LOGS
  );
  const canViewActivityLogs = canView || canList;

  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [total, setTotal] = useState(0);

  const [filter, setFilter] = useState("type");
  const [searchText, setSearchText] = useState("");
  const [adminIdInput, setAdminIdInput] = useState("");

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const { afterToday } = DateRangePicker;
  const { dateRangeValue, changeDateRangeHandler } = useDateTimePicker({
    onDateSelect: (_start, _end) => {
      setStartDate(_start);
      setEndDate(_end);
    },
  });

  const now = new Date();
  const prevMonth = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    now.getDate()
  );

  // ✅ API Call
  const { mutate: getData, isLoading } = useMutation(client.admin.logs, {
    onSuccess: (data) => {
      const response = data?.data ?? data;
      const totalCount =
        response?.total ??
        response?.totalcount ??
        response?.totalCount ??
        data?.total ??
        data?.totalcount ??
        0;
      setTotal(totalCount);
      setSearchParams((prev) => ({
        ...prev,
        per_page:
          response?.per_page ||
          response?.limit ||
          data?.per_page ||
          data?.limit ||
          LIST_PAGE_SIZE,
      }));

      const list =
        (Array.isArray(response?.data) ? response?.data : null) ??
        (Array.isArray(response?.items) ? response?.items : null) ??
        (Array.isArray(response?.logs) ? response?.logs : null) ??
        (Array.isArray(response) ? response : null) ??
        (Array.isArray(data?.data) ? data?.data : null) ??
        (Array.isArray(data?.items) ? data?.items : null) ??
        (Array.isArray(data?.logs) ? data?.logs : null) ??
        (Array.isArray(data) ? data : []);

      setDataSource(
        list?.map((item: any) => ({
          key: item.id || item._id,
          ...item,
          createdAt: item?.created_at ?? item?.createdAt ?? "__",
          updatedAt: item?.updated_at ?? item?.updatedAt ?? "__",
        })) || []
      );
    },
    onError: (error: any) => {
      const message =
        error.code === "ERR_NETWORK"
          ? NetworkDisconnectMsg
          : error.response
            ? getErrorMessage(error.response.data)
            : APISomethingWrongMsg;
      setAlertText(message);
      setIsAlertOpened(true);
    },
  });

  // ✅ Fetch Data on filters/pagination change
  useEffect(() => {
    if (canViewActivityLogs) {
      getData({
        ...searchParams,
        search_field: filter || undefined,
        search_value: searchText || undefined,
        start_date: startDate,
        end_date: endDate,
        admin_id: searchParams.admin_id || undefined,
      });
    }
  }, [
    canViewActivityLogs,
    searchParams.current_page,
    startDate,
    endDate,
    searchText,
    searchParams.admin_id,
  ]);

  // ✅ Debounced Admin ID filter
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchParams((prev) => ({
        ...prev,
        current_page: 1,
        admin_id: adminIdInput.trim() || undefined,
      }));
    }, 500);
    return () => clearTimeout(handler);
  }, [adminIdInput]);

  const onPageChange = (page: number) =>
    setSearchParams((prev) => ({ ...prev, current_page: page }));

  // ✅ Table Columns
  const columns: ColumnsType<any> = [
    {
      title: "Admin ID",
      dataIndex: "admin_id",
      key: "admin_id",
      render: (value) => (
        <p className="text-primary text-sm font-medium">{value}</p>
      ),
    },
    {
      title: "API URL",
      dataIndex: "api_url",
      key: "api_url",
      render: (value) => <p className="text-secondary text-sm">{value}</p>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (value) => <p className="text-secondary text-sm">{value}</p>,
    },
    {
      title: "Text",
      dataIndex: "text",
      key: "text",
      render: (value) => <p className="text-secondary text-sm">{value}</p>,
    },
    {
      title: "Body",
      dataIndex: "body",
      key: "body",
      render: (value: any) =>
        value && typeof value === "object" && !Array.isArray(value) ? (
          <div className="text-sm text-primary">
            {Object.entries(value).map(([k, v]) => (
              <p key={k}>
                {k}: {String(v)}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-primary text-sm">—</p>
        ),
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      render: (value) => <p className="text-sm text-primary">{value}</p>,
    },
    {
      title: "User Agent",
      dataIndex: "user_agent",
      key: "user_agent",
      render: (value) => <p className="text-sm text-secondary">{value}</p>,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => (
        <p className="text-sm text-primary">
          {value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "__"}
        </p>
      ),
    },
  ];

  return (
    <div className="w-full h-full bg-gray">
      <div className="p-4">
        <h1 className="text-2xl sm:text-3xl font-manrope text-primary font-medium">
          Admin Management
        </h1>

        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <SearchFilter
            options={adminLogsFilterOptions}
            placeholder="Search by type or IP address"
            value={filter}
            onChange={setFilter}
            searchText={searchText}
            setSearchText={setSearchText}
          />

          <input
            type="text"
            placeholder="Search by admin ID"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={adminIdInput}
            onChange={(e) => setAdminIdInput(e.target.value)}
          />

          <DateRangePicker
            format="dd/MM/yyyy"
            size="lg"
            block
            placement="bottomEnd"
            style={{ width: "308px" }}
            value={dateRangeValue}
            disabledDate={afterToday?.()}
            onChange={(range) => {
              setSearchParams((prev) => ({ ...prev, current_page: 1 }));
              if (range) {
                const [start, end] = currentTimeForDataPicker(range);
                changeDateRangeHandler([start, end]);
              } else {
                changeDateRangeHandler(null);
              }
            }}
            defaultCalendarValue={[prevMonth, now]}
            ranges={[]}
          />
        </div>

        {!canViewActivityLogs ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-xl p-8 mt-4 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view admin activity logs.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl p-4 mt-4">
            <Table
              loading={isLoading}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                total,
                current: searchParams.current_page,
                onChange: onPageChange,
                position: ["bottomCenter"],
                defaultPageSize: LIST_PAGE_SIZE,
                showSizeChanger: false,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
